import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Clock,
  ClipboardList,
  Receipt,
  Wallet,
  Scale,
  BarChart3,
  Settings,
  Menu,
  LogOut,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";

const navSections = [
  {
    title: null,
    items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }],
  },
  {
    title: "Gestión de Personal",
    items: [
      { to: "/empleados", icon: Users, label: "Empleados" },
      { to: "/asistencia", icon: Clock, label: "Asistencia", alertsKey: true },
      { to: "/incidencias", icon: ClipboardList, label: "Incidencias" },
    ],
  },
  {
    title: "Planilla y Pagos",
    items: [
      { to: "/nomina", icon: Receipt, label: "Nomina" },
      { to: "/vales", icon: Wallet, label: "Vales" },
      { to: "/contratos", icon: Scale, label: "Contratos" },
    ],
  },
  {
    title: "Análisis",
    items: [{ to: "/reportes", icon: BarChart3, label: "Reportes" }],
  },
  {
    title: "Sistema",
    items: [{ to: "/configuracion", icon: Settings, label: "Configuracion" }],
  },
];

function SidebarContent({ onNavigate, user, onLogout, alertsCount }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-3">
        <img
          src="/dadao-logo.png"
          alt="DADAO · VETCOMER WORKFORCE SYSTEM"
          className="h-7 w-auto"
          data-testid="sidebar-logo"
        />
      </div>
      <Separator className="bg-zinc-200" />
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-5">
          {navSections.map((section, si) => (
            <div key={si} className="space-y-0.5">
              {section.title && (
                <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                  {section.title}
                </p>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-zinc-900 text-white shadow-sm"
                        : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                    }`
                  }
                  data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                  <span className="flex-1">{item.label}</span>
                  {item.alertsKey && alertsCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none"
                      data-testid="sidebar-alerts-badge"
                    >
                      {alertsCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </ScrollArea>
      <Separator className="bg-zinc-200" />
      {user && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-zinc-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600">
                {(user.name || "U")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {user.name}
              </p>
              <p className="text-[11px] text-zinc-400 truncate">{user.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-zinc-500 hover:text-red-600 hover:bg-red-50 h-8 text-xs"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Cerrar sesion
          </Button>
        </div>
      )}
      {!user && (
        <div className="px-5 py-4">
          <p className="text-[11px] text-zinc-400 font-medium">VETCOMER LTDA</p>
          <p className="text-[11px] text-zinc-300">Costa Rica</p>
        </div>
      )}
    </div>
  );
}

export default function Layout({ user, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);

  // Poll pending HR alerts count every 30s
  useEffect(() => {
    let mounted = true;
    const load = () => {
      api
        .get("/hr-alerts/pending-count")
        .then((r) => { if (mounted) setAlertsCount(r.data?.count || 0); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 w-64 h-screen border-r border-zinc-200 bg-white/80 backdrop-blur-xl flex-col z-30">
        <SidebarContent user={user} onLogout={onLogout} alertsCount={alertsCount} />
      </aside>

      {/* Desktop top-bar with notification bell */}
      <div className="hidden md:flex fixed top-0 right-0 left-64 h-12 bg-white/70 backdrop-blur-md border-b border-zinc-200 items-center justify-end px-6 z-20">
        <NotificationBell />
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white/90 backdrop-blur-md border-b border-zinc-200 flex items-center px-4 z-30">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5 text-zinc-700" />
              {alertsCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SidebarContent
              onNavigate={() => setMobileOpen(false)}
              user={user}
              onLogout={onLogout}
              alertsCount={alertsCount}
            />
          </SheetContent>
        </Sheet>
        <div className="flex items-center ml-3 gap-2 flex-1">
          <img
            src="/dadao-logo.png"
            alt="DADAO · VETCOMER WORKFORCE SYSTEM"
            className="h-5 w-auto"
            data-testid="topbar-logo"
          />
        </div>
        <NotificationBell compact />
      </div>

      {/* Main Content */}
      <main className="md:ml-64 p-5 sm:p-8 pt-[72px] md:pt-[64px] min-h-screen max-w-[1600px]">
        <Outlet />
      </main>
    </div>
  );
}
